import { isNull, isUndefined, noop } from 'lodash/fp';
import React, { useCallback, useMemo } from 'react';

import { IconButton } from '../../../shared/button/IconButton';
import { Icon } from '../../../shared/icon/Icon';
import { Tick, Delete, SortDescending, SortAscending, Sort } from '../../../shared/icons';
import { SortOrder } from '../../../shared/table/types';
import { dateFormatter } from '../../../shared/table/arkTableFormatters';
import { OverflowTooltip } from '../../../shared/tooltip';
import { BreakdownColumnSort, CustomDocumentQueryAnalytics, SelectedClientOrUser, BreakdownList } from './store';
import styles from './ChatBot.module.scss';

const { red, green, white } = styles;

export const initialBreakdownColumnSort: BreakdownColumnSort = {
    'name': null,
    'totalQuestions': null,
    'totalSuitable': null
};

export const initialUserBreakdownColumnSort: BreakdownColumnSort = {
    'question': null,
    'suitable': null,
    'dateAsked': null
};

interface ChatBotBreakdownProps {
    customQueryAnalytics: CustomDocumentQueryAnalytics;
    setBreakdownColumnOrder: (value: BreakdownColumnSort) => void;
    setUserBreakdownColumnOrder: (value: BreakdownColumnSort) => void;
    breakdownColumnOrder: BreakdownColumnSort;
    userBreakdownColumnOrder: BreakdownColumnSort;
    clientOrUserKeys: string[];
    selectedClient: SelectedClientOrUser | null;
    selectedUser: SelectedClientOrUser | null;
    setSelectedClientOrUser: (id: number, name: string) => void;
}

export const ChatBotBreakdown: React.FC<ChatBotBreakdownProps> = ({
    customQueryAnalytics,
    setBreakdownColumnOrder,
    setUserBreakdownColumnOrder,
    breakdownColumnOrder,
    userBreakdownColumnOrder,
    clientOrUserKeys,
    selectedClient,
    selectedUser,
    setSelectedClientOrUser
}) => {

    const { questionsAskedPerClient, questionsAskedPerClientUser, questionsAskedByUser } = customQueryAnalytics;

    const breakdownList = useMemo(() => {
        let list: BreakdownList[] = [];
        if (isNull(selectedClient) && questionsAskedPerClient) {
            list = clientOrUserKeys.map(key => ({ name: key, ...questionsAskedPerClient[key] }));
        }
        if (!isNull(selectedClient) && isNull(selectedUser) && questionsAskedPerClientUser) {
            list = clientOrUserKeys.map(key => ({ name: key, ...questionsAskedPerClientUser[key] }));
        }
        list = list.sort((a, b) => a.name.localeCompare(b.name));
        const nameColumnOrder = breakdownColumnOrder['name'];
        const totalAskedColumnOrder = breakdownColumnOrder['totalQuestions'];
        const totalSuitableColumnOrder = breakdownColumnOrder['totalSuitable'];
        if (nameColumnOrder === SortOrder.DESCENDING) {
            list = list.sort((a, b) => b.name.localeCompare(a.name));
        }
        if (totalAskedColumnOrder === SortOrder.ASCENDING) {
            list = list.sort((a, b) => a.totalAsked - b.totalAsked);
        }
        if (totalAskedColumnOrder === SortOrder.DESCENDING) {
            list = list.sort((a, b) => b.totalAsked - a.totalAsked);
        }
        if (totalSuitableColumnOrder === SortOrder.ASCENDING) {
            list = list.sort((a, b) => a.totalAsked - b.totalAsked);
        }
        if (totalSuitableColumnOrder === SortOrder.DESCENDING) {
            list = list.sort((a, b) => b.totalAsked - a.totalAsked);
        }
        return list;
    }, [selectedClient, selectedUser, clientOrUserKeys, questionsAskedPerClient, questionsAskedPerClientUser, breakdownColumnOrder]);

    const userBreakdownList = useMemo(() => {
        let list = (questionsAskedByUser || []).sort((a, b) => a.questionAsked.localeCompare(b.questionAsked));
        const questionColumnOrder = userBreakdownColumnOrder['question'];
        const suitableColumnOrder = userBreakdownColumnOrder['suitable'];
        const dateAskedColumnOrder = userBreakdownColumnOrder['dateAsked'];
        if (questionColumnOrder === SortOrder.DESCENDING) {
            list = list.sort((a, b) => b.questionAsked.localeCompare(a.questionAsked));
        }
        if (suitableColumnOrder === SortOrder.ASCENDING) {
            list = list.sort((a, b) => a.acceptedByCheck - b.acceptedByCheck);
        }
        if (suitableColumnOrder === SortOrder.DESCENDING) {
            list = list.sort((a, b) => b.acceptedByCheck - a.acceptedByCheck);
        }
        if (dateAskedColumnOrder === SortOrder.ASCENDING) {
            list = list.sort((a, b) => a.dateAsked.localeCompare(b.dateAsked));
        }
        if (dateAskedColumnOrder === SortOrder.DESCENDING) {
            list = list.sort((a, b) => b.dateAsked.localeCompare(a.dateAsked));
        }
        return list;
    }, [questionsAskedByUser, userBreakdownColumnOrder]);

    const getBreakdownSortIcon = useCallback((column: string, isUser = false) => {
        const columnOrder = isUser ? userBreakdownColumnOrder[column] : breakdownColumnOrder[column];
        if (!isNull(columnOrder)) {
            return columnOrder === SortOrder.ASCENDING ? SortDescending : SortAscending;
        }
        return Sort;
    }, [breakdownColumnOrder, userBreakdownColumnOrder]);

    const sortBreakdownColumn = useCallback((column: string, isUser = false) => {
        const columnOrder = isUser ? userBreakdownColumnOrder[column] : breakdownColumnOrder[column];
        let orderToSort = isNull(columnOrder) ? SortOrder.DESCENDING : SortOrder.ASCENDING;
        if (columnOrder) {
            orderToSort = columnOrder === SortOrder.ASCENDING ? SortOrder.DESCENDING : SortOrder.ASCENDING;
        }
        const initialValue = isUser ? initialUserBreakdownColumnSort : initialBreakdownColumnSort;
        const updatedColumnOrders = { ...initialValue, [column]: orderToSort };
        isUser ? setUserBreakdownColumnOrder(updatedColumnOrders) : setBreakdownColumnOrder(updatedColumnOrders);
    }, [breakdownColumnOrder, userBreakdownColumnOrder, setBreakdownColumnOrder, setUserBreakdownColumnOrder]);

    const breakdown = useMemo(() => {
        if (!isUndefined(questionsAskedByUser)) {
            return (
                <>
                    {[{ questionAsked: 'Question Asked', acceptedByCheck: 'Suitable Question', dateAsked: 'Date Asked' }, ...userBreakdownList].map(({ questionAsked, acceptedByCheck, dateAsked }, index) => (
                        <div className={styles.userQuestionWrapper} key={index}>
                            <div className={styles.questionAsked}>
                                <OverflowTooltip overlayText={questionAsked} />
                                {index === 0 &&
                                    <div className={styles.sortWrapper}>
                                        <IconButton icon={getBreakdownSortIcon('question', true)} onClick={() => sortBreakdownColumn('question', true)} color={white} fontSize={20} />
                                    </div>
                                }
                            </div>
                            <div className={styles.questionSuitable}>
                                {index === 0 ? acceptedByCheck : <Icon icon={acceptedByCheck === 1 ? Tick : Delete} color={acceptedByCheck === 1 ? green : red} />}
                                {index === 0 &&
                                    <div className={styles.sortWrapper}>
                                        <IconButton icon={getBreakdownSortIcon('suitable', true)} onClick={() => sortBreakdownColumn('suitable', true)} color={white} fontSize={20} />
                                    </div>
                                }
                            </div>
                            <div className={styles.dateAsked}>
                                <OverflowTooltip overlayText={dateAsked === 'Date Asked' ? dateAsked : dateFormatter(dateAsked)} />
                                {index === 0 &&
                                    <div className={styles.sortWrapper}>
                                        <IconButton icon={getBreakdownSortIcon('dateAsked', true)} onClick={() => sortBreakdownColumn('dateAsked', true)} color={white} fontSize={20} />
                                    </div>
                                }
                            </div>
                        </div>
                    ))}
                </>
            );
        }
        return (
            <>
                {[{ name: `${isNull(selectedClient) ? 'Client' : 'User'} Name`, totalAsked: 'Total Questions Asked', totalPassed: 'Total Suitable Questions', id: 0 }, ...breakdownList].map(({ name, totalAsked, totalPassed, id }, index) => (
                    <div className={styles.breakdownList} key={index} onClick={() => index === 0 ? noop : setSelectedClientOrUser(id, name)}>
                        <div className={styles.breakdownName}>
                            {name}
                            {index === 0 && <div className={styles.sortWrapper}>
                                <IconButton icon={getBreakdownSortIcon('name')} onClick={() => sortBreakdownColumn('name')} color={white} fontSize={20} />
                            </div>}
                        </div>
                        <div className={styles.breakdownTotalAsked}>
                            {totalAsked}
                            {index === 0 && <div className={styles.sortWrapper}>
                                <IconButton icon={getBreakdownSortIcon('totalQuestions')} onClick={() => sortBreakdownColumn('totalQuestions')} color={white} fontSize={20} />
                            </div>}
                        </div>
                        <div className={styles.breakdownTotalPassed}>
                            {totalPassed}
                            {index === 0 && <div className={styles.sortWrapper}>
                                <IconButton icon={getBreakdownSortIcon('totalSuitable')} onClick={() => sortBreakdownColumn('totalSuitable')} color={white} fontSize={20} />
                            </div>}
                        </div>
                    </div>
                ))}
            </>
        );

    }, [breakdownList, questionsAskedByUser, userBreakdownList, setSelectedClientOrUser, selectedClient, getBreakdownSortIcon, sortBreakdownColumn]);

    return breakdown;
};
